import {
  Alert,
  Anchor,
  Box,
  Checkbox,
  NativeSelect,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { CONSENT_TEXT_MAX, parseConsentText } from '@quizee/core';
import { Link } from 'react-router-dom';
import { SIGNUP_BUTTON_MAX, SIGNUP_HEADING_MAX, type SignupDraft } from '../interaction/draft';
import {
  defaultListName,
  listName,
  resolvedSignupConsent,
  signupWouldVanish,
  type NewsletterContext,
} from '../interaction/newsletter';
import { ConsentPreview } from './ConsentSection';
import { OverrideField } from './OverrideField';
import { ReadList, ReadRow, sourceWords } from './ReadValue';
import { ConsentSegments } from './WidgetPreview';

/**
 * The newsletter signup step on one interaction.
 *
 * One switch and four optional fields, and almost all of the thinking is in what is deliberately
 * not here. Whether an address is confirmed before it joins the list is not asked, because that is
 * a property of the list and the list belongs to the masthead: the same address arriving from two
 * quizzes under the same list is one subscription, so answering it twice could only produce a
 * disagreement. It is shown all the same, because an editor switching this on is entitled to know
 * what happens to the addresses they are about to collect.
 *
 * The wording is an override rather than a field, on the same reasoning: almost every signup should
 * say what the masthead says, so the masthead's sentence is what appears, named, and writing one
 * here is the deliberate move for the one off case. The list is the same shape again, a picker
 * rather than a free choice, because a masthead with a daily briefing, a sport newsletter and a
 * weekend read wants most signups joining the everyday one without anybody having to say so.
 */

/** How a signup reads on the interaction's own screen, before anybody presses Edit. */
export function SignupRead({
  signup,
  newsletter,
}: {
  signup: SignupDraft;
  newsletter: NewsletterContext;
}) {
  if (!signup.enabled) {
    return (
      <Box>
        <Text component="h3" fw={500} size="sm" mb="xs">
          Newsletter signup
        </Text>
        <Text size="sm" c="dimmed">
          Not offered. Readers finish this without being asked for an email address.
        </Text>
      </Box>
    );
  }

  const wording = resolvedSignupConsent(signup.consentText, newsletter.consent.value);
  const vanishes = signupWouldVanish(wording);

  return (
    <Box>
      <Text component="h3" fw={500} size="sm" mb="xs">
        Newsletter signup
      </Text>
      <ReadList>
        <ReadRow
          label="Offered when readers finish"
          value={
            signup.heading === ''
              ? 'Yes, with the wording the widget brings with it'
              : signup.heading
          }
          note={signup.buttonLabel === '' ? undefined : `The button says: ${signup.buttonLabel}`}
        />
        <ReadRow
          label="Joins the list"
          value={
            signup.listId === null
              ? defaultListName(newsletter.lists)
              : listName(newsletter.lists, signup.listId)
          }
          source={signup.listId === null ? undefined : 'chosen for this interaction'}
          note={
            signup.listId === null
              ? `${newsletter.brandName}'s default list. A reader who has already joined it from another interaction is not asked again.`
              : undefined
          }
        />
        <ReadRow
          label="What readers agree to"
          value={vanishes ? 'Nothing' : <ConsentSegments segments={parseConsentText(wording)} />}
          source={sourceWords(signup.consentText !== null, newsletter.consent.from)}
        />
        <ReadRow
          label="What happens to the address"
          value={newsletter.optIn === 'double' ? 'Double opt in' : 'Single opt in'}
          source={`set on ${newsletter.brandName}`}
          note={optInWords(newsletter.optIn)}
        />
      </ReadList>
      {vanishes && <VanishWarning newsletter={newsletter} />}
    </Box>
  );
}

/** The controls, drawn inside the interaction form. */
export function SignupSection({
  signup,
  onChange,
  newsletter,
}: {
  signup: SignupDraft;
  onChange: (signup: SignupDraft) => void;
  newsletter: NewsletterContext;
}) {
  const overridden = signup.consentText !== null;
  const wording = resolvedSignupConsent(signup.consentText, newsletter.consent.value);
  const vanishes = signup.enabled && signupWouldVanish(wording);

  return (
    <section aria-labelledby="signup-heading">
      <Text component="h3" id="signup-heading" fw={500} size="sm">
        Newsletter signup
      </Text>
      <Text c="dimmed" size="xs" mb="xs">
        Readers who have finished can be asked for an email address. It joins whichever of{' '}
        {newsletter.brandName}&apos;s lists you pick below, or the default list if you leave it.
      </Text>

      <Stack gap="md">
        <Checkbox
          id="signup-enabled"
          checked={signup.enabled}
          onChange={(event) => onChange({ ...signup, enabled: event.currentTarget.checked })}
          label="Offer readers the newsletter when they have finished"
          description="Off for most interactions. Ask under the pieces where a reader has just shown you what they are interested in."
        />

        {signup.enabled && (
          <>
            <TextInput
              id="signup-heading-text"
              label="Heading"
              description="Optional. What the signup step is called. Left empty, readers get the widget's own wording."
              value={signup.heading}
              maxLength={SIGNUP_HEADING_MAX}
              onChange={(event) => onChange({ ...signup, heading: event.currentTarget.value })}
              style={{ maxWidth: '28rem' }}
            />
            <TextInput
              id="signup-button-label"
              label="Button"
              description="Optional. What the button says, such as Sign me up."
              value={signup.buttonLabel}
              maxLength={SIGNUP_BUTTON_MAX}
              onChange={(event) => onChange({ ...signup, buttonLabel: event.currentTarget.value })}
              style={{ maxWidth: '18rem' }}
            />
            <NativeSelect
              id="signup-list"
              label="List"
              description="Which of the masthead's lists this joins. Leave it on the default unless this signup is its own campaign."
              value={signup.listId ?? ''}
              onChange={(event) =>
                onChange({
                  ...signup,
                  listId: event.target.value === '' ? null : event.target.value,
                })
              }
              data={[
                { value: '', label: `${defaultListName(newsletter.lists)} (the default list)` },
                ...newsletter.lists
                  .filter((list) => !list.is_default)
                  .map((list) => ({
                    value: list.list_id,
                    label: list.archived ? `${list.name} (archived)` : list.name,
                  })),
              ]}
              style={{ maxWidth: '24rem' }}
            />

            <Stack component="ul" gap="sm" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              <OverrideField
                label="Newsletter wording"
                overridden={overridden}
                inheritedFrom={newsletter.consent.from}
                canEdit
                onOverride={() => onChange({ ...signup, consentText: newsletter.consent.value })}
                onInherit={() => onChange({ ...signup, consentText: null })}
              >
                <Textarea
                  id="signup-consent-text"
                  label="Newsletter wording"
                  description="What readers agree to when they tick the box. A link, bold and italic are allowed, and anything else is removed when you save."
                  autosize
                  minRows={2}
                  maxRows={6}
                  maxLength={CONSENT_TEXT_MAX}
                  disabled={!overridden}
                  value={wording}
                  onChange={(event) =>
                    onChange({ ...signup, consentText: event.currentTarget.value })
                  }
                />
                <Box mt="sm">
                  <ConsentPreview text={wording} />
                </Box>
              </OverrideField>
            </Stack>

            <Text size="sm" c="dimmed">
              {newsletter.optIn === 'double' ? 'Double opt in' : 'Single opt in'} on{' '}
              {newsletter.brandName}. {optInWords(newsletter.optIn)}{' '}
              <Anchor component={Link} to={`/settings/brands/${newsletter.brandId}`} size="sm">
                Change it for the whole masthead
              </Anchor>
              .
            </Text>

            {vanishes && <VanishWarning newsletter={newsletter} />}
          </>
        )}
      </Stack>
    </section>
  );
}

/**
 * The one state that would look like a bug.
 *
 * Publishing drops a signup whose wording comes out empty, and it is right to: asking somebody to
 * tick a box means showing them what they are agreeing to. Saving is refused rather than allowed
 * with a warning, because a step that vanished silently at publish is exactly the sort of thing a
 * publisher would find out about from a reader. This says so before the refusal arrives.
 */
function VanishWarning({ newsletter }: { newsletter: NewsletterContext }) {
  return (
    <Alert role="alert" color="yellow" variant="light" radius="md" mt="sm">
      <Text size="sm">
        There is nothing to show readers beside the tick box, so this signup cannot be saved and
        would never reach a reader. Asking somebody to agree to something means showing them what.
      </Text>
      <Text size="sm" mt="xs">
        Write the wording above, or set one for {newsletter.brandName}:{' '}
        <Anchor component={Link} to={`/settings/brands/${newsletter.brandId}`} size="sm">
          the brand&apos;s newsletter wording
        </Anchor>
        .
      </Text>
    </Alert>
  );
}

function optInWords(optIn: 'single' | 'double'): string {
  return optIn === 'double'
    ? 'An address waits until the reader opens a confirmation email, so some never join.'
    : 'An address joins the list as soon as it is typed, checked or not.';
}
